import { Controller } from "@hotwired/stimulus"
import { Chart, registerables } from 'chart.js'
import 'chartjs-adapter-date-fns'
import annotationPlugin from 'chartjs-plugin-annotation'

Chart.register(...registerables, annotationPlugin)

export default class extends Controller {
  static values = {
    datasets: Array, // Expected format: [{ label: 'Name', data: [{x, y, milestone}], color: '#hex' }, ...]
    unit: { type: String, default: 'mg/dL' }
  }

  connect() {
    const ctx = this.element.getContext('2d');
    
    // Helper to convert hex to rgba
    const hexToRgba = (hex, alpha) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    // Create gradient helper
    const createGradient = (color) => {
      const gradient = ctx.createLinearGradient(0, 0, 0, 400);
      gradient.addColorStop(0, hexToRgba(color, 0.4));
      gradient.addColorStop(1, hexToRgba(color, 0.0));
      return gradient;
    };

    // Calculate date range to determine appropriate time unit
    let timeUnit = 'month';

    if (this.datasetsValue.length > 0 && this.datasetsValue[0].data.length > 0) {
      const dates = this.datasetsValue[0].data.map(d => new Date(d.x));
      const minDate = new Date(Math.min(...dates));
      const maxDate = new Date(Math.max(...dates));
      const rangeInMonths = (maxDate - minDate) / (1000 * 60 * 60 * 24 * 30);

      if (rangeInMonths > 36) {
        // More than 3 years: show years only
        timeUnit = 'year';
      }
      // Otherwise: keep months (cleaner than quarters for health data)
    }

    // Process annotations from milestones
    const annotations = {};
    if (this.datasetsValue.length > 0) {
      // Iterate the first dataset since milestones are per-reading (per-date)
      this.datasetsValue[0].data.forEach(point => {
        if (point.milestone) {
          const dateKey = point.x;

          annotations[`milestone-${dateKey}`] = {
            type: 'line',
            xMin: dateKey,
            xMax: dateKey,
            borderColor: '#10b981', // emerald-500
            borderWidth: 2,
            borderDash: [4, 4],
            z: -1, // Draw behind datasets
            label: {
              display: true,
              content: point.milestone,
              position: 'start',
              backgroundColor: 'rgba(236, 253, 245, 0.95)', // emerald-50
              borderColor: '#a7f3d0', // emerald-200
              borderWidth: 1,
              borderRadius: 4,
              color: '#065f46', // emerald-800
              font: {
                family: "'IBM Plex Sans', sans-serif",
                size: 10,
                weight: '600'
              },
              padding: 6,
              yAdjust: 0
            }
          };
        }
      });
    }

    this.chart = new Chart(this.element, {
      type: 'line',
      data: {
        datasets: this.datasetsValue.map(dataset => ({
          label: dataset.label,
          data: dataset.data,
          borderColor: dataset.color,
          borderWidth: 2.5,
          fill: false,
          tension: 0.3,
          pointRadius: 5,
          pointHoverRadius: 7,
          pointBackgroundColor: dataset.color, // Full fill color
          pointBorderWidth: 0, // No border needed if full fill
          pointBorderColor: dataset.color,
          pointHoverBackgroundColor: dataset.color,
          pointHoverBorderColor: dataset.color,
          pointHoverBorderWidth: 0,
          hitRadius: 20,
          hoverRadius: 7
        }))
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false,
          axis: 'x'
        },
        scales: {
          x: {
            type: 'time',
            time: {
              unit: timeUnit,
              displayFormats: {
                month: 'MMM yyyy',
                year: 'yyyy'
              }
            },
            grid: {
              display: false
            },
            ticks: {
              autoSkip: true,
              maxTicksLimit: 8
            }
          },
          y: {
            min: 0,
            grid: {
              color: '#f1f5f9',
              borderDash: [5, 5]
            },
            border: {
              display: false
            },
            title: {
              display: true,
              text: this.unitValue
            }
          }
        },
        plugins: {
          annotation: {
            annotations: annotations
          },
          subtitle: {
            display: true,
            text: '', // Empty subtitle for spacing
            padding: {
              bottom: 10
            }
          },
          legend: {
            display: true,
            position: 'top',
            align: 'end',
            labels: {
              usePointStyle: true,
              boxWidth: 15, // Increased from 8 to add "space in front" of text
              padding: 20,
              font: {
                family: "'Source Sans 3', sans-serif",
                size: 12,
                weight: 500
              }
            }
          },
          tooltip: {
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
            titleColor: '#0f172a',
            bodyColor: '#334155',
            borderColor: '#e2e8f0',
            borderWidth: 1,
            padding: 12,
            boxPadding: 4,
            usePointStyle: true,
            titleFont: {
              size: 13,
              weight: 'bold'
            },
            bodyFont: {
              size: 12
            },
            footerFont: {
              size: 11,
              weight: 'normal',
              style: 'italic'
            },
            footerColor: '#64748b',
            footerMarginTop: 8,
            callbacks: {
              footer: (tooltipItems) => {
                const itemWithMilestone = tooltipItems.find(item =>
                  item.raw && item.raw.milestone
                );

                if (itemWithMilestone) {
                  return `📍 ${itemWithMilestone.raw.milestone}`;
                }
                return '';
              }
            }
          }
        }
      }
    })
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
    }
  }
}
